// src/pages/EditProfilePage.js
import React, { useContext, useState, useEffect } from "react";
import { LanguageContext } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { getMe, updateMe } from "../services/users.api";
import TopBanner from "../components/TopBanner";
import MainHeader from "../components/MainHeader";
import Footer from "../components/Footer";

function EditProfilePage() {
  const { lang } = useContext(LanguageContext);
  const navigate = useNavigate();

  const [form, setForm] = useState({
  full_name: "",
  phone: "",
  email: "",
  city: "",      // governorate
  area: "",
  street: "",
  building: "",
});

  const [phoneTouched, setPhoneTouched] = useState(false);
  const [uiError, setUiError] = useState("");
const [loading, setLoading] = useState(false);
const [profileLoading, setProfileLoading] = useState(true);
const [profileLoadFailed, setProfileLoadFailed] = useState(false);

// ---------------- LEBANON GOVERNORATES ----------------
const LEB_GOV = [
  { en: "Beirut", ar: "بيروت" },
  { en: "Mount Lebanon", ar: "جبل لبنان" },
  { en: "North Lebanon", ar: "لبنان الشمالي" },
  { en: "South Lebanon", ar: "لبنان الجنوبي" },
  { en: "Bekaa", ar: "البقاع" },
  { en: "Baalbek-Hermel", ar: "بعلبك الهرمل" },
  { en: "Nabatieh", ar: "النبطية" },
  { en: "Akkar", ar: "عكار" },
];

const normalizeGovernorate = (value = "") => {
  const raw = String(value || "").trim();

  if (!raw) return "";

  const match = LEB_GOV.find(
    (gov) =>
      gov.en.toLowerCase() === raw.toLowerCase() ||
      gov.ar === raw
  );

  return match ? match.en : raw;
};

  /* ================= PHONE FORMATTING (UNCHANGED) ================= */

  const formatLbLocal = (digits) => {
    const d = digits.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    return [p1, p2, p3].filter(Boolean).join(" ");
  };

  const formatLbPlus961 = (digitsAfter961) => {
    const d = digitsAfter961.slice(0, 8);
    const p1 = d.slice(0, 2);
    const p2 = d.slice(2, 5);
    const p3 = d.slice(5, 8);
    const parts = [p1, p2, p3].filter(Boolean).join(" ");
    return parts ? `+961 ${parts}` : "+961";
  };

  const formatPhoneInput = (raw) => {
    const v = (raw || "").trim();

    if (v.startsWith("+961")) {
      const after = v.replace(/\s/g, "").slice(4);
      const digits = after.replace(/\D/g, "");
      return formatLbPlus961(digits);
    }

    const first = v.replace(/\s/g, "").charAt(0);
    if (first === "0" || first === "7" || first === "8") {
      const digits = v.replace(/\D/g, "").slice(0, 8);
      return formatLbLocal(digits);
    }

    return raw;
  };

  const validatePhone = (value) => {
    const v = (value || "").trim();

    if (v.startsWith("+961")) {
      const compact = v.replace(/\s/g, "");
      const digits = compact.slice(4).replace(/\D/g, "");
      if (digits.length !== 8) {
        return lang === "AR"
          ? "يجب أن يبدأ الرقم بـ +961 ويتبعه 8 أرقام."
          : "Phone must be +961 followed by 8 digits.";
      }
      return null;
    }

    const compact = v.replace(/\s/g, "");
    const first = compact.charAt(0);

    if (first === "0" || first === "7" || first === "8") {
      const digits = compact.replace(/\D/g, "");
      if (digits.length !== 8) {
        return lang === "AR"
          ? "يجب أن يتكون رقم الهاتف من 8 أرقام."
          : "Phone must be exactly 8 digits.";
      }
      return null;
    }

    return null;
  };

  /* ================= LOAD USER ================= */

  useEffect(() => {
  let mounted = true;

  async function loadUser() {
    try {
      setProfileLoading(true);
      setProfileLoadFailed(false);

      const user = await getMe();

      if (!mounted) return;

      if (!user) {
        setProfileLoadFailed(true);
        return;
      }

      const rawPhone = user.phone || "";
      const rawEmail = user.email || "";
      const phoneLooksLikeEmail = rawPhone.includes("@");

      const normalizedUser = {
  ...user,
  phone: phoneLooksLikeEmail ? "" : rawPhone,
  email: rawEmail || (phoneLooksLikeEmail ? rawPhone : ""),
  city: normalizeGovernorate(user.city || ""),
  area: user.area || "",
  street: user.street || "",
  building: user.building || "",
};

      setForm(normalizedUser);
      localStorage.setItem("esill-user", JSON.stringify(normalizedUser));
    } catch (err) {
      console.error("Failed to load user profile", err);

      if (mounted) {
        setProfileLoadFailed(true);
      }
    } finally {
      if (mounted) {
        setProfileLoading(false);
      }
    }
  }

  loadUser();

  return () => {
    mounted = false;
  };
}, []);

  /* ================= TEXT ================= */

  const TEXT = {
    EN: {
      mainPage: "Main page",
      account: "Account",
      title: "Edit profile",
      subtitle: "Update your personal information and address details.",
      save: "SAVE CHANGES",
      saving: "SAVING...",
      required: "Full name and phone number are required.",
      personalInfo: "Personal information",
      addressInfo: "Address information",
      placeholders: {
        full_name: "Full Name",
        phone: "Phone Number",
        email: "Email",
        city: "Governorate",
area: "Area",
street: "Street",
building: "Building",
loadingProfile: "Loading your profile...",
profileUnavailable: "We could not load your profile.",
backToAccount: "Back to account",
tryAgain: "Try again",
      },
    },
    AR: {
      mainPage: "الصفحة الرئيسية",
      account: "الحساب",
      title: "تعديل الملف",
      loadingProfile: "جاري تحميل ملفك الشخصي...",
profileUnavailable: "لم نتمكن من تحميل ملفك الشخصي.",
backToAccount: "العودة إلى الحساب",
tryAgain: "إعادة المحاولة",
      subtitle: "قم بتحديث معلوماتك الشخصية وتفاصيل العنوان.",
      save: "حفظ التغييرات",
      saving: "جارٍ الحفظ...",
      required: "الاسم الكامل ورقم الهاتف مطلوبان.",
      personalInfo: "المعلومات الشخصية",
      addressInfo: "معلومات العنوان",
      placeholders: {
        full_name: "الاسم الكامل",
        phone: "رقم الهاتف",
        email: "البريد الإلكتروني",
        city: "المحافظة",
area: "المنطقة",
street: "الشارع",
building: "المبنى",
      },
    },
  };

  const t = TEXT[lang];

  const handleChange = (key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  };

  const save = async () => {
    setPhoneTouched(true);
    setUiError("");

    if (!form.full_name?.trim() || !form.phone?.trim()) {
      setUiError(t.required);
      return;
    }

    const phoneError = validatePhone(form.phone);
    if (phoneError) {
      setUiError(phoneError);
      return;
    }

    try {
      setLoading(true);

      const updated = await updateMe({
  full_name: form.full_name,
  phone: form.phone,
  email: form.email,
  city: form.city,
  area: form.area,
  street: form.street,
  building: form.building,
});

      localStorage.setItem("esill-user", JSON.stringify(updated));
      navigate("/account");
    } catch (err) {
      setUiError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */

  const inputClass = `
  w-full
  h-11
  border border-gray-400
  bg-white
  px-3
  text-sm
  outline-none
  focus:border-black
  transition
  disabled:bg-gray-50
  disabled:text-gray-500
  disabled:cursor-wait
`;

  const labelClass = "block text-[12px] font-medium text-gray-900 mb-1";

  const buttonClass = `
    w-full
    h-11
    rounded-md
    border-2 border-black
    bg-white
    text-black
    text-sm font-semibold tracking-wide
    hover:bg-black hover:text-white
    active:scale-[0.99]
    transition
    disabled:opacity-50
    disabled:cursor-not-allowed
  `;

  const EditProfileSkeleton = () => (
  <div
    className="w-full min-h-screen bg-[#F6EFE9] overflow-x-hidden"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <main className="bg-[#F6EFE9] px-4 md:px-8 md:pt-[175px]">
      <div className="h-4 w-[240px] bg-gray-200 animate-pulse rounded mb-6" />
      <div className="h-6 w-[170px] bg-gray-200 animate-pulse rounded mb-3" />
      <div className="h-[1px] bg-gray-200 mx-1 md:mx-4 mb-8" />

      <div className="max-w-5xl mx-auto pb-12">
        <div className="mb-8">
          <div className="h-5 w-[360px] max-w-full bg-gray-200 animate-pulse rounded" />
        </div>

        <div className="grid md:grid-cols-[1fr_4px_1fr] gap-8 md:gap-10">
          <section>
            <div className="h-6 w-[170px] bg-gray-200 animate-pulse rounded mb-5" />

            <div className="space-y-5">
              {[1, 2, 3].map((i) => (
                <div key={i}>
                  <div className="h-3 w-[90px] bg-gray-200 animate-pulse rounded mb-2" />
                  <div className="h-11 w-full bg-gray-200 animate-pulse rounded" />
                </div>
              ))}
            </div>
          </section>

          <div className="hidden md:block w-[4px] bg-gray-200 min-h-[320px]" />

          <section>
            <div className="h-6 w-[170px] bg-gray-200 animate-pulse rounded mb-5" />

            <div className="space-y-5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <div className="h-3 w-[90px] bg-gray-200 animate-pulse rounded mb-2" />
                  <div className="h-11 w-full bg-gray-200 animate-pulse rounded" />
                </div>
              ))}

              <div className="h-11 w-full bg-gray-200 animate-pulse rounded" />
            </div>
          </section>
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

const ProfileLoadFailedState = () => (
  <div
    className="w-full min-h-screen bg-[#F6EFE9] overflow-x-hidden"
    dir={lang === "AR" ? "rtl" : "ltr"}
  >
    <TopBanner />
    <MainHeader />

    <main className="bg-[#F6EFE9] px-4 md:px-8 md:pt-[175px]">
      <div className="max-w-3xl mx-auto pb-16">
        <div className="border border-gray-200 bg-[#F6EFE9] rounded-2xl p-6 md:p-8 shadow-[0_10px_24px_rgba(0,0,0,0.04)]">
          <div
            className="
              w-[58px] h-[58px]
              rounded-full
              border border-black
              flex items-center justify-center
              text-[28px]
              text-black
              mb-5
            "
          >
            !
          </div>

          <h1 className="text-[26px] md:text-[34px] font-bold text-black">
            {t.profileUnavailable}
          </h1>

          <p className="mt-3 text-[14px] md:text-[15px] leading-7 text-gray-600">
            {lang === "AR"
              ? "يرجى تسجيل الدخول من جديد أو العودة إلى صفحة الحساب."
              : "Please sign in again or return to your account page."}
          </p>

          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="
                h-11
                px-6
                border-2 border-black
                rounded-md
                bg-black
                text-white
                text-sm font-semibold tracking-wide
                hover:opacity-90
                transition
              "
            >
              {t.tryAgain}
            </button>

            <button
              type="button"
              onClick={() => navigate("/account")}
              className="
                h-11
                px-6
                border-2 border-black
                rounded-md
                bg-white
                text-black
                text-sm font-semibold tracking-wide
                hover:bg-black hover:text-white
                transition
              "
            >
              {t.backToAccount}
            </button>
          </div>
        </div>
      </div>
    </main>

    <Footer />
  </div>
);

if (profileLoading) {
  return <EditProfileSkeleton />;
}

if (profileLoadFailed) {
  return <ProfileLoadFailedState />;
}

  return (
    <div
      className="w-full min-h-screen bg-[#F6EFE9] overflow-x-hidden"
      dir={lang === "AR" ? "rtl" : "ltr"}
    >
      <TopBanner />
      <MainHeader />

      <main className="bg-[#F6EFE9] px-4 md:px-8 md:pt-[175px]">
        {/* BREADCRUMB */}
        <div className="text-[12px] md:text-sm text-gray-900 mb-6">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="hover:text-black hover:underline"
          >
            {t.mainPage}
          </button>

          <span className="mx-2">›</span>

          <button
            type="button"
            onClick={() => navigate("/account")}
            className="hover:text-black hover:underline"
          >
            {t.account}
          </button>

          <span className="mx-2">›</span>

          <span className="text-gray-900">{t.title}</span>
        </div>

        {/* TITLE */}
        <div className={`${lang === "AR" ? "text-right" : "text-left"} mb-2`}>
          <h1 className="text-[16px] md:text-[20px] font-normal tracking-wide text-gray-900 uppercase">
            {t.title}
          </h1>
        </div>

        {/* LINE */}
        <div className="h-[1px] bg-gray-300 mx-1 md:mx-4 mb-8" />

        {/* CONTENT */}
        <div className="max-w-5xl mx-auto pb-12">
          <div className="mb-8">
            <h2 className=" hidden text-[30px] md:text-[38px] font-black text-black leading-tight mb-3">
              {t.title}
            </h2>

            <p className="text-sm md:text-base text-gray-600">
              {t.subtitle}
            </p>
          </div>

          {uiError && (
            <div className="mb-6 border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-md">
              {uiError}
            </div>
          )}

          <div className="grid md:grid-cols-[1fr_4px_1fr] gap-8 md:gap-10">
            {/* PERSONAL INFO */}
            <section>
              <h3 className="text-[18px] font-bold text-black mb-5">
                {t.personalInfo}
              </h3>

              <div className="space-y-5">
                <div>
                  <label className={labelClass}>
                    {t.placeholders.full_name}
                  </label>
                  <input
  disabled={loading}
  value={form.full_name}
  onChange={(e) =>
    handleChange("full_name", e.target.value)
  }
  className={inputClass}
/>
                </div>

                <div>
                  <label className={labelClass}>
                    {t.placeholders.phone}
                  </label>
                  <input
  disabled={loading}
  value={form.phone}
  onChange={(e) =>
    handleChange("phone", formatPhoneInput(e.target.value))
  }
  onBlur={() => setPhoneTouched(true)}
  className={inputClass}
/>

                  {phoneTouched && validatePhone(form.phone) && (
                    <p className="text-xs text-red-600 mt-2">
                      {validatePhone(form.phone)}
                    </p>
                  )}
                </div>

                <div>
                  <label className={labelClass}>
                    {t.placeholders.email}
                  </label>
                  <input
  disabled={loading}
  value={form.email}
  onChange={(e) => handleChange("email", e.target.value)}
  className={inputClass}
/>
                </div>
              </div>
            </section>

            {/* DESKTOP LINE */}
            <div className="hidden md:block w-[4px] bg-gray-300 min-h-[320px]" />

            {/* ADDRESS INFO */}
            <section>
              <h3 className="text-[18px] font-bold text-black mb-5">
                {t.addressInfo}
              </h3>

              <div className="space-y-5">
                <div>
  <label className={labelClass}>
    {t.placeholders.city}
  </label>

  <select
    disabled={loading}
    value={form.city}
    onChange={(e) => handleChange("city", e.target.value)}
    className={inputClass}
  >
    <option value="">
      {lang === "EN" ? "Select Governorate" : "اختر المحافظة"}
    </option>

    {LEB_GOV.map((gov) => (
      <option key={gov.en} value={gov.en}>
        {lang === "EN" ? gov.en : gov.ar}
      </option>
    ))}
  </select>
</div>

<div>
  <label className={labelClass}>
    {t.placeholders.area}
  </label>
  <input
    disabled={loading}
    value={form.area}
    onChange={(e) => handleChange("area", e.target.value)}
    className={inputClass}
  />
</div>

<div>
  <label className={labelClass}>
    {t.placeholders.street}
  </label>
  <input
    disabled={loading}
    value={form.street}
    onChange={(e) => handleChange("street", e.target.value)}
    className={inputClass}
  />
</div>

<div>
  <label className={labelClass}>
    {t.placeholders.building}
  </label>
  <input
    disabled={loading}
    value={form.building}
    onChange={(e) => handleChange("building", e.target.value)}
    className={inputClass}
  />
</div>

                <button
                  type="button"
                  onClick={save}
                  disabled={loading}
                  className={buttonClass}
                >
                  {loading ? t.saving : t.save}
                </button>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default EditProfilePage;